import { animate, state, style, transition, trigger } from "@angular/animations";
import { ChangeDetectorRef, Component, HostListener, Inject, OnInit, ViewChild } from "@angular/core";
import { ActivationEnd, NavigationEnd, NavigationError, NavigationStart, Router } from "@angular/router";
import { MessageMaster } from "@app/shared/messagemaster/messagemaster.component";
import { TitleBlinker } from "@appcore/_helpers";
import { AuthenticationService, AuthorizationService, ColorUtility, ProgressStatus } from "@appcore/service";
import { DocumentInterruptSource, Idle, StorageInterruptSource } from "@ng-idle/core";
import { OKTA_AUTH } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { ConfirmationService, MessageService } from "primeng/api";
import { BehaviorSubject, Subscription } from "rxjs";
import config from "./app.config";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.css"],
	animations: [
		trigger("overlayState", [
			state("hidden", style({ opacity: 0 })),
			state("visible", style({ opacity: 1 })),
			transition("visible => hidden", animate("400ms ease-in")),
			transition("hidden => visible", animate("400ms ease-out"))
		])
	],
	providers: [
		ConfirmationService,
		MessageService, //Do NOT remove. Required for all message master and toast master calls
		ProgressStatus,
		TitleBlinker
	]
})
export class AppComponent implements OnInit {
	_token: any;
	_currentTime: Date;
	_expiresTime: Date;
	_expiresAtMinusFiveMinutes: Date;

	adminIdle: number = config.systemIdle.admin;
	defaultIdle: number = config.systemIdle.default;
	adminTimeout: number = config.systemTimeout.admin;
	clientTimeout: number = config.systemTimeout.client;

	currentTime = new Date();
	timestamp = Date.parse(this.currentTime.toString());
	activeSession = false;

	// session state
	idleState = "NOT_STARTED";
	countdown?: number = null;
	lastPing?: Date = null;
	timedOut = false;
	adminAccess = false;

	hasAdminRole = false;
	loggedOut = true;

	isAuthenticated: BehaviorSubject<boolean>;
	userRoles: string;

	loading = false;
	processed = false;

	error: Error | null = null;

	activeMenuId: string;
	menuActive: boolean;

	public subscription: Subscription;
	Status: any = {
		Header: "Progress Status",
		Message: "Loading profile..."
	};

	@ViewChild("statusDialog", { static: true }) statusDialog: {
		visible: boolean;
	};

	@ViewChild("catalogmenutitle") catalogmenutitle;
	validationView = false;

	@ViewChild("appMenu") appMenu;

	@HostListener("click") onClick() {
		this.populateTimers();
		if (new Date(Date.now()) > this._expiresAtMinusFiveMinutes) {
			this.reset();
		}
	}

	constructor(
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		private idle: Idle,
		private titleBlinker: TitleBlinker,
		public authenticationService: AuthenticationService,
		public authorizationService: AuthorizationService,
		public cd: ChangeDetectorRef,
		public colorUtil: ColorUtility,
		public confirmationService: ConfirmationService,
		public messageMaster: MessageMaster,
		public progressStatus: ProgressStatus,
		public router: Router
	) {
		// sets a default idle timeout of 15 minutes
		idle.setIdle(this.defaultIdle);
		// sets a default timeout period of 30 minutes. after 30 minutes of inactivity, the user will be considered timed out.
		idle.setTimeout(this.clientTimeout);
		// sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
		idle.setInterrupts(this.createCustomInterruptSources(null));
		// do something when the user becomes idle
		idle.onIdleStart.subscribe(() => {
			this.displayIdleNotification();
			this.idleState = "IDLE";
			console.debug(`${this.idleState} ${new Date()}`);
			this.titleBlinker.blink("**SESSION IDLE - PLS HUB**", 12);
			this.confirmIdleSessionState();
		});
		// do something when the user is no longer idle
		idle.onIdleEnd.subscribe(() => {
			this.idleState = "NOT_IDLE";
			this.reset();
			console.debug(`${this.idleState} ${new Date()}`);
			this.countdown = null;
			cd.detectChanges(); // how do i avoid this kludge?
		});
		// do something when the user has timed out
		idle.onTimeout.subscribe(() => {
			this.timedOut = true;
			this.idleState = "TIMED_OUT";
			this.endSession();
		});
		// do something as the timeout countdown does its thing
		idle.onTimeoutWarning.subscribe(seconds => {
			this.countdown = seconds;
			if (this.countdown === 300) {
				this.displayIdleNotification();
			}
		});

		this.router.events.subscribe(event => {
			if (event instanceof NavigationStart) {
				console.log(`Nav Start - examining URL for GUIDs: ${event.url.toString()}`);

				// case insensitive
				const guidRegExCI = new RegExp(/[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/i);
				// upper only
				const guidRegEx = new RegExp(/[{]?[0-9A-F]{8}-([0-9A-F]{4}-){3}[0-9A-F]{12}[}]?$/g);

				if (event.url.match(guidRegExCI)) {
					// if matches any GUID
					if (!event.url.match(guidRegEx)) {
						// if NOT upper
						const urlFragments = event.url.split("/");
						if (urlFragments.length > 0) {
							for (const frag in urlFragments) {
								if (urlFragments[frag].match(guidRegExCI)) {
									// if its a guid, uppercase it
									urlFragments[frag] = urlFragments[frag].toUpperCase();
								}
							}
							console.log(`GUIDS found, converted: ${urlFragments.join("/")}`);
						}
						this.router.navigate(urlFragments, {
							replaceUrl: true,
							queryParamsHandling: "preserve"
						});
					}
				}
			}

			if (event instanceof ActivationEnd) {
				// Hide progress spinner or progress bar
				// this.currentRoute = event.url;
				// console.log(event);
			}

			if (event instanceof NavigationEnd) {
				// Hide progress spinner or progress bar
				// this.currentRoute = event.url;
				// console.log(event);
			}

			if (event instanceof NavigationError) {
				// Hide progress spinner or progress bar
				// Present error to user
				console.log(event.error);
			}
		});
	}

	/**
	 * Confirm the user wants to stay active while working in the HUB for nearly an entire session.
	 */
	confirmActiveSession() {
		this.populateTimers();
		this.confirmationService.confirm({
			icon: "pi pi-exclamation-triangle",
			header: "Extend Session",
			message: `Your session will expire at ${this._expiresTime}. Do you want to keep working?`,
			accept: () => {
				setTimeout(() => {
					this.messageMaster.message({
						severity: "warn",
						summary: "Extend",
						detail: `Your session has been extended.`
					});
					this.reset();
				}, 500);
			},
			reject: () => {
				setTimeout(() => {
					this.messageMaster.message({
						severity: "warn",
						summary: "Log Out",
						detail: `You're being redirected to the login page.`
					});
				}, 500);
				this.authenticationService.logout();
			}
		});
	}

	/**
	 * Confirm intent of user to keep their session active after returning from an idle state.
	 */
	confirmIdleSessionState() {
		if (this._token && this._token.accessToken) {
			this.populateTimers();
		}
		this.confirmationService.close();
		this.confirmationService.confirm({
			icon: "pi pi-exclamation-triangle",
			key: "idleSession",
			header: "Extend Session",
			message:
				this._expiresTime != null
					? `Your session is idle. If you take no action your session will expire at ${this._expiresTime}. Try to extend your session?`
					: "Try to extend your session?",
			accept: () => {
				setTimeout(() => {
					this.messageMaster.message({
						severity: "warn",
						summary: "Extend",
						detail: `Your session has been extended.`
					});
					this.reset();
				}, 500);
			},
			reject: () => {
				setTimeout(() => {
					this.messageMaster.message({
						severity: "warn",
						summary: "Log Out",
						detail: `You're being redirected to the login page.`
					});
				}, 500);
				this.authenticationService.logout();
			}
		});
	}

	/**
	 * Define User Interrupt Sources which can stop a session from being idle.
	 * @param options
	 * @returns
	 */
	createCustomInterruptSources(options) {
		return [
			new DocumentInterruptSource("keydown mousedown touchstart touchmove scroll", options),
			new StorageInterruptSource(options)
		];
	}

	/**
	 * Handle Idle Notifications with Notification Permission Check
	 */
	displayIdleNotification() {
		if ("Notification" in window) {
			console.debug(`Notification Permission: ${Notification.permission}`);
			if (!["granted", "denied"].includes(Notification.permission)) {
				Notification.requestPermission();
			} else {
				if (Notification.permission === "granted") {
					this.generateWindowsNotification("idle");
				}
			}
		}
	}

	/**
	 * Generate Windows Notification - Session Extension
	 * @param state - Session State
	 */
	generateWindowsNotification(state: string) {
		if (state == "idle") {
			const notif = new Notification("New Message", {
				icon: "https://learning.childrenshospitals.org/hub/favicon.png",
				body: "Your PLS Hub session is about to expire. Click here to continue working."
			});
		}
	}

	/**
	 * we'll call this method when we want to start/reset the idle process
	 * reset any component state and be sure to call idle.watch()
	 */
	reset(): void {
		this.idle.watch();
		this.idleState = "NOT_IDLE";
		this.oktaAuth.tokenManager
			.renew("accessToken")
			.then(function (res) {
				this._token = JSON.parse(localStorage.getItem("okta-token-storage"));
				this.populateTimers();
				console.debug("Updated Expiry: ", this._expiresTime); // Obtain renewed access token - Extends logged in access
				this.countdown = null;
				this.lastPing = null;
				this.oktaAuth.session.refresh();
			})
			.catch(function (err) {
				//console.error(err);
			});

		this.clearMessages();
	}

	async ngOnInit(): Promise<void> {
		console.log("Getting Current User");
		this.authenticationService.currentUserValue(); //initialize default current user value
		this.isAuthenticated = this.authenticationService.$authed;
		this.populateUserInfo();
	}

	endSession(): void {
		// End Session for Active/Inactive User
		this.confirmationService.confirm({
			icon: "pi pi-exclamation-triangle",
			key: "expiredSession",
			header: "Confirm Sign-out",
			message: `Your session has expired. For security you will need to log back in to resume working in the HUB.`,
			accept: () => {
				this.confirmationService.close();
				this.messageMaster.message({
					severity: "warn",
					summary: "Confirmed",
					detail: `You're being redirected to the login page.`
				});
				this.authenticationService.logout();
			}
		});
	}

	clearMessages(): void {
		this.messageMaster.clear();
	}

	populateUserInfo(): void {
		this.authenticationService.getCurrentUserValue().subscribe(currentUser => {
			this.userRoles = currentUser.Groups as string;

			if (this.isAuthenticated) {
				this.populateTimers();
				this.timedOut = false;
				setTimeout(() => {
					if ("Notification" in window) {
						if (!["granted", "denied"].includes(Notification.permission)) {
							Notification.requestPermission();
							console.info("Requesting Notification Permission");
						}
					} else {
						console.info("Notifications unavailable");
					}
				}, 15000);
			} else {
				this.idle.stop();
				this.endSession();
			}

			if (this.userRoles.includes("PLS_Hub_Admins")) {
				console.log("-- Admin Access Granted --");
				this.idle.setIdle(this.adminIdle);
				this.idle.setTimeout(this.adminTimeout);
				this.adminAccess = true;
			} else {
				console.log("-- Client Access Granted --");
			}
		});
	}

	/**
	 * Populate timers for session expiration, early session expiration warning timer and current time.
	 */
	populateTimers(): void {
		if (this.authenticationService.$authed.value) {
			this._token = JSON.parse(localStorage.getItem("okta-token-storage"));
			if (this._token && this._token.accessToken.expiresAt != undefined) {
				console.debug("Token Found: Updating session timers...");
				this._expiresTime = new Date(this._token.accessToken.expiresAt * 1000);
				this._expiresAtMinusFiveMinutes = new Date(this._expiresTime.getTime() - 10 * 30 * 1000);
			} else {
				this.endSession();
			}
		}
	}

	onMenuButtonClick(event: Event): void {
		this.menuActive = !this.menuActive;
		if (!this.menuActive) {
			this.activeMenuId = null;
		}
		event.preventDefault();
	}

	onMenuButtonGroupClick(event: Event): void {
		this.menuActive = true;
	}

	closeMenu(): void {
		this.menuActive = false;
	}

	shouldHaveMenu(): boolean {
		let allowed = false;
		if (this.authenticationService.$authed.value) {
			allowed = true;
		}
		return allowed;
	}

	dataProgressStart(): void {
		this.Status.Message = this.progressStatus.getSentMessage();
		this.loading = true;
	}
	dataProgressUpdate(): void {
		this.Status.Message = this.progressStatus.getReceivedMessage();
	}
	dataProgressComplete(): void {
		setTimeout(() => {
			this.messageMaster.clear();
		}, 500);
		this.loading = false;
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}
